import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import ordinal from 'ordinal-js';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  values, max, findIndex, each, concat, orderBy,
  filter, range, find, slice, camelCase, snakeCase,
} from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import styles from '../CurrentStats/styles.css';
import NumberDisplay from '../../../../components/number-display';
import MoneyDisplay from '../../../../components/money-display';
import StatsCell from '../CurrentStats/StatsCell';
import { getUser } from '../../../Auth/AuthReducer';
import ProgressBar from '../../../../components/progress-bar';
import Clickable from './Clickable';
import LabelHeaderRenderer from './LabelHeaderRenderer';
import wrapperStyles from './styles.css';

import {
  getWinLossStats, getWinLossOwnerStats,
  getWinLossLoading, getWinLossLoaded,
  getWinLossReasonStats,

  getWinLossGlobalRankingsLoading,
  getWinLossGlobalRankingsLoaded,
  getWinLossGlobalRankings,

} from '../../DashboardReducer';

const WIN_CELL = 'WIN_CELL';
const LOST_CELL = 'LOST_CELL';
const NO_DECISION_CELL = 'NO_DECISION_CELL';
const PER_PAGE = 3;

function AmountRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const comp = (
    <MoneyDisplay value={value} abbreviated />
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function LabelRenderer(props) {
  const { value, data, cellType, onSelectOpportunities } = props;
  const { isUnknown } = data;
  let comp = (<span>{value}</span>);

  if (!isUnknown) {
    const tooltip = (
      <Tooltip id={`${cellType}-${data.id}-tips`}>{value}</Tooltip>
    );
    comp = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tooltip}>
        <span>{value}</span>
      </OverlayTrigger>
    );
  }
  
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function FreqRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const { isUnknown } = data;
  const precision = isUnknown ? 0 : 1;
  const suffix = isUnknown ? null : '%';
  const comp = (
    <NumberDisplay value={value} precision={precision} suffix={suffix} />
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

function RankingRenderer(props) {
  const { value, data, onSelectOpportunities } = props;
  const { isUnknown } = data;
  
  if (isUnknown) {
    return null;
  }
  const comp = (
    <div className="text-right">
      {value}<sup>{ordinal.ordinalSuffix(value)}</sup>
    </div>
  );
  return (
    <Clickable
      opportunityIds={data.opportunityIds}
      onSelect={onSelectOpportunities}
    >
      {comp}
    </Clickable>
  );
}

class WinLossStats extends Component {
  constructor() {
    super();
    this.state = {
      heights: {
        [WIN_CELL]: 0,
        [LOST_CELL]: 0,
        [NO_DECISION_CELL]: 0,
      },
      pages: {
        [WIN_CELL]: 1,
        [LOST_CELL]: 1,
        [NO_DECISION_CELL]: 1,
      },
      cellWidth: 0,
      maxHeight: 0,
    };
    this.freqColumnValueGetter = this.freqColumnValueGetter.bind(this);
    this.getRowClass = this.getRowClass.bind(this);
    this.loadPage = this.loadPage.bind(this);
  }
  handleCellHeightChange(type, height) {
    const { heights } = this.state;
    const newHeights = {
      ...heights,
      [type]: Math.ceil(height),
    };
    const maxHeight = max(values(newHeights));
    this.setState({
      heights: newHeights,
      maxHeight,
    });
  }

  freqColumnValueGetter(params) {
    const { data } = params;
    if (!data) {
      return null;
    }
    return data.isUnknown ? data.count : data.frequency * 100;
  }

  getRowClass(params) {
    if (params.node.rowPinned) {
      return styles.pinned;
    }
  }

  
  
  loadPage(cellType, deltaPage = 1) {
    const currentPage = this.state.pages[cellType];
    const reasons = this.getRasonsFromCellType(cellType);
    const totalPages = Math.ceil(reasons.length / PER_PAGE);
    let nextPage = currentPage + deltaPage;
    
    if (deltaPage > 0 && nextPage > totalPages) {
        nextPage = 1;
    }
    if (deltaPage < 0 && nextPage <= 0) {
      nextPage = totalPages;
    }
    this.setState({ pages: { ...this.state.pages, [cellType]: nextPage } });
  }

  getRasonsFromCellType(cellType) {
    const {
      stats, loading, loaded,
      rankings, myRankings, reasonStats,
    } = this.props;
    const isLoading = loading || !loaded;
    let reasons = [];

    if (!isLoading && stats) {
      if (cellType === WIN_CELL) {
        reasons = reasonStats.won || [];
      } else if (cellType === LOST_CELL) {
        reasons = reasonStats.lost || [];
      } else if (cellType === NO_DECISION_CELL) {
        reasons = reasonStats.noDecision || [];
      }
    }
    return reasons;
  }

  renderReasons(title, reasons, cellType) {
    const { feedbackLoaded } = this.props;
    
    if (!feedbackLoaded) {
      return (
        <ProgressBar />
      );
    }
    
    const currentPage = this.state.pages[cellType];

    const { cellWidth } = this.state;
    const minHeight = 44 * 5;
    const allGoodReasons = filter(reasons, { isUnknown: false });

    const goodReasons = slice(allGoodReasons, (currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
    const unknownReasons = slice(filter(reasons, { isUnknown: true }), 0, 1); // just to make sure;
    
    const freqWidth = 70;
    const amountWidth = 100;
    const rankingWidth = 60;
    const totalPage = Math.ceil(allGoodReasons.length / PER_PAGE);

    const columnDefs = [
      {
        headerName: '',
        field: 'order',
        suppressMenu: true,
        width: rankingWidth,
        cellRendererSelector: (params) => {
          const { isUnknown } = params.data;
          if (isUnknown) {
            return {
              component: LabelHeaderRenderer,
              params: {
                isPrevious: false,
                isNext: true,
                currentPage,
                cellType,
                totalPage,
                onSelectPage: this.loadPage,
              },
            };
          }
          return {
            component: RankingRenderer,
          };
        },
        headerComponent: LabelHeaderRenderer,
        headerComponentParams: {
          isPrevious: true,
          isNext: false,
          currentPage,
          cellType,
          totalPage,
          onSelectPage: this.loadPage,
        },
      },
      {
        headerName: title,
        field: 'label',
        cellRenderer: LabelRenderer,
        suppressMenu: true,
        width: cellWidth - amountWidth - freqWidth - rankingWidth - 4,
        cellRendererParams: {
          cellType,
          onSelectOpportunities: this.props.handleSelectOpportunities,
        },
      },
      {
        headerName: 'Freq',
        field: 'frequency',
        width: freqWidth,
        cellRenderer: FreqRenderer,
        type: 'numericColumn',
        suppressMenu: true,
        valueGetter: this.freqColumnValueGetter,
        cellRendererParams: {
          onSelectOpportunities: this.props.handleSelectOpportunities,
        },
      },
      {
        headerName: 'Amount',
        field: 'amount',
        width: amountWidth,
        cellRenderer: AmountRenderer,
        type: 'numericColumn',
        suppressMenu: true,
        cellRendererParams: {
          onSelectOpportunities: this.props.handleSelectOpportunities,
        },
      },
    ];
    return (
      <div className={`${styles.rankTable} ${wrapperStyles.table} ag-theme-alpine`} style={{ height: minHeight, width: '100%' }} >
        <AgGridReact
          reactNext
          enableColResize
          columnDefs={columnDefs}
          rowData={goodReasons}
          pinnedBottomRowData={unknownReasons}
          getRowClass={this.getRowClass}
        />
      </div>
    );
  }

  renderCell(type) {
    const {
      stats, loading, loaded,
      myRankings,
      rankingLoading, rankingLoaded,
      wonRankings, lostRankings, noDecisionRankings,
    } = this.props;
    const isLoading = loading || !loaded;
    const isRankingLoading = rankingLoading || !rankingLoaded;
    let amount = null;
    let count = null;
    let ids = [];
    let cellRankings = [];
    let ranking = null;
    let amountRate = null;
    let countRate = null;
    const reasons = this.getRasonsFromCellType(type);

    if (!isLoading && stats) {
      if (type === WIN_CELL) {
        count = stats.wonCount;
        amount = stats.wonAmount;
        ids = stats.wonIds;
        amountRate = stats.wonAmountRate;
        countRate = stats.wonCountRate;
        cellRankings = wonRankings;
        ranking = myRankings.won;
      } else if (type === LOST_CELL) {
        count = stats.lostCount;
        amount = stats.lostAmount;
        ids = stats.lostIds;
        amountRate = stats.lostAmountRate;
        countRate = stats.lostCountRate;
        cellRankings = lostRankings;
        ranking = myRankings.lost;
      } else if (type === NO_DECISION_CELL) {
        count = stats.noDecisionCount;
        amount = stats.noDecisionAmount;
        ids = stats.noDecisionIds;
        amountRate = stats.noDecisionAmountRate;
        countRate = stats.noDecisionCountRate;
        cellRankings = noDecisionRankings;
        ranking = myRankings.noDecision;
      }
    }

    let statsNumberClick = null;
    if (ids && ids.length > 0) {
      statsNumberClick = () => {
        this.props.handleSelectOpportunities(ids, type);
      };
    }
    let title = null;
    let cls = null;
    let reasonTitle = 'Reasons';
    let rateTitle = null;

    if (type === WIN_CELL) {
      title = 'Won';
      reasonTitle = 'Win Reasons';
      rateTitle = 'win rate';
      cls = styles.wonCell;
    } else if (type === LOST_CELL) {
      title = 'Lost';
      reasonTitle = 'Loss Reasons';
      rateTitle = 'loss rate';
      cls = styles.lostCell;
    } else if (type === NO_DECISION_CELL) {
      title = 'No Decision';
      reasonTitle = 'No Decision Reasons';
      rateTitle = 'No decision rate';
      cls = styles.noDecisionCell;
    }


    return (
      <StatsCell
        className={cls}
        title={title}
        rateTitle={rateTitle}
        isLoading={isLoading}
        rank={ranking}
        rankings={cellRankings}
        isRankingLoading={isRankingLoading}
        sumAmount={amount}
        sumCount={count}
        amountRate={amountRate * 100}
        countRate={countRate * 100}
        sumCountOnClick={statsNumberClick}
        sumAmountOnClick={statsNumberClick}
        minHeight={this.state.maxHeight}
        onHeightChange={(height) => {
          this.handleCellHeightChange(type, height);
        }}
        onWidthChange={(cellWidth) => {
          this.setState({ cellWidth });
        }}
      >
        {count > 0 && this.renderReasons(reasonTitle, reasons, type)}
      </StatsCell>
    );
  }

  render() {
    const { hasNoDecisionSetup } = this.props;
    return (
      <div className={styles.stats}>
        {this.renderCell(WIN_CELL)}
        {this.renderCell(LOST_CELL)}
        {hasNoDecisionSetup && this.renderCell(NO_DECISION_CELL)}
      </div>
    );
  }
}

WinLossStats.propTypes = {
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  stats: PropTypes.shape({}),
  allStats: PropTypes.shape({}),
  myRankings: PropTypes.shape({
    won: PropTypes.number,
    lost: PropTypes.number,
    noDecision: PropTypes.number,
  }),
  handleSelectOpportunities: PropTypes.func,
  hasNoDecisionSetup: PropTypes.bool,
  reasonStats: PropTypes.shape({
    won: PropTypes.arrayOf(PropTypes.shape({})),
    lost: PropTypes.arrayOf(PropTypes.shape({})),
    noDecision: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  feedbackLoaded: PropTypes.bool,
  wonRankings: PropTypes.arrayOf(PropTypes.shape({})),
  lostRankings: PropTypes.arrayOf(PropTypes.shape({})),
  noDecisionRankings: PropTypes.arrayOf(PropTypes.shape({})),
  rankingLoading: PropTypes.bool,
  rankingLoaded: PropTypes.bool,
};
WinLossStats.defaultProps = {
  loading: false,
  loaded: false,
  stats: {},
  allStats: {},
  handleSelectOpportunities: () => { },
  hasNoDecisionSetup: false,
  myRankings: {
    won: null,
    lost: null,
    noDecision: null,
  },
  reasonStats: {
    won: [],
    lost: [],
    noDecision: [],
  },
  feedbackLoaded: false,
  rankingLoading: false,
  rankingLoaded: false,
  wonRankings: [],
  lostRankings: [],
  noDecisionRankings: [],
};
WinLossStats.contextTypes = {
  router: PropTypes.shape({}),
};


// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  const { shouldShowAllStats } = ownProps;

  const auth = getUser(state);
  const allRankings = getWinLossGlobalRankings(state);
  const rankingLoading = getWinLossGlobalRankingsLoading(state);
  const rankingLoaded = getWinLossGlobalRankingsLoaded(state);

  const wonRankings = orderBy(allRankings.map(owner => ({ owner, amount: owner.won_amount })), ['amount'], ['desc']);
  const lostRankings = orderBy(allRankings.map(owner => ({ owner, amount: owner.lost_amount })), ['amount'], ['desc']);
  const noDecisionRankings = orderBy(allRankings.map(owner => ({ owner, amount: owner.no_decision_amount })), ['amount'], ['desc']);

  const myRankings = {
    won: null,
    lost: null,
    noDecision: null,
  };

  const userId = auth ? auth.user_id : null;
  if (userId) {
    each(['won', 'lost', 'noDecision'], (stage) => {
      // myRankings[stage] = myRankIdx + 1;
      let stageRankings = [];
      if (stage === 'won') {
        stageRankings = wonRankings;
      } else if (stage === 'lost') {
        stageRankings = lostRankings;
      } else if (stage === 'noDecision') {
        stageRankings = noDecisionRankings;
      }
      const myRankIdx = findIndex(stageRankings, r => r.owner.user_id === userId);
      if (myRankIdx >= 0) {
        myRankings[stage] = myRankIdx + 1;
      }
    });
  }
  const allStats = getWinLossStats(state);

  let stats = null;
  if (shouldShowAllStats) {
    stats = allStats;
  } else if (auth.user_id) {
    stats = getWinLossOwnerStats(state)[auth.user_id];
  }
  // TODO: check if this is a manager, if not, only return hist own stats
  const reasonStats = getWinLossReasonStats(state);
  return {
    loading: getWinLossLoading(state),
    loaded: getWinLossLoaded(state),
    stats,
    allStats,
    myRankings,
    rankingLoading,
    rankingLoaded,
    wonRankings,
    lostRankings,
    noDecisionRankings,
    reasonStats,
  };
}

export default connect(mapStateToProps)(WinLossStats);
